import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { MailCheck, Send, X, ShieldOff, ShieldCheck } from 'lucide-react';

const UserDetailDrawer = ({ userId, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgSubject, setMsgSubject] = useState('');
  const [msgBody, setMsgBody] = useState('');
  const [msgSeverity, setMsgSeverity] = useState('normal');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load user');
    }
  };

  useEffect(() => { if (userId) load(); }, [userId]);

  const forceVerify = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/verify_email`);
      toast.success('Email marked as verified.');
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const patch = async (updates) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, updates);
      toast.success('Updated');
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const sendMessage = async () => {
    if (!msgSubject.trim() || !msgBody.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/message`, {
        subject: msgSubject.trim(),
        body: msgBody.trim(),
        severity: msgSeverity,
      });
      toast.success('Message sent — user will see it in /support and receive an email.');
      setMsgOpen(false);
      setMsgSubject(''); setMsgBody(''); setMsgSeverity('normal');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!userId) return null;
  const u = detail?.user;
  const c = detail?.counts || {};

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-user-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <aside className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="p-5 border-b border-zita-taupe/40 flex items-center justify-between">
          <div>
            <div className="font-serif text-xl text-zita-espresso">User detail</div>
            <div className="text-xs text-zita-mocha">Full snapshot from Zita database</div>
          </div>
          <button type="button" onClick={onClose} data-testid="admin-user-drawer-close" className="p-2 rounded hover:bg-zita-sand">
            <X size={16} />
          </button>
        </div>
        {!detail && <div className="p-5 text-sm text-zita-mocha">Loading…</div>}
        {u && (
          <div className="p-5 space-y-4">
            <div>
              <div className="text-lg font-serif text-zita-espresso">{u.email}</div>
              {u.display_name && <div className="text-sm text-zita-mocha">{u.display_name}</div>}
              {u.username && <div className="text-xs text-zita-cocoa">@{u.username}</div>}
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="zita-card p-3">
                <div className="text-zita-mocha uppercase tracking-wider">Role</div>
                <div className="text-zita-espresso font-medium">{u.is_admin ? 'admin' : u.role}</div>
              </div>
              <div className="zita-card p-3">
                <div className="text-zita-mocha uppercase tracking-wider">Subscription</div>
                <div className="text-zita-espresso font-medium">{u.subscription_status || 'none'}</div>
              </div>
              <div className="zita-card p-3">
                <div className="text-zita-mocha uppercase tracking-wider">Email verified</div>
                <div className="text-zita-espresso font-medium">{u.email_verified ? 'Yes' : 'No'}</div>
              </div>
              <div className="zita-card p-3">
                <div className="text-zita-mocha uppercase tracking-wider">Super Admin</div>
                <div className="text-zita-espresso font-medium">{u.is_admin ? 'Yes' : 'No'}</div>
              </div>
              <div className="zita-card p-3">
                <div className="text-zita-mocha uppercase tracking-wider">Suspended</div>
                <div className="text-zita-espresso font-medium">{u.suspended ? 'Yes' : 'No'}</div>
              </div>
              <div className="zita-card p-3">
                <div className="text-zita-mocha uppercase tracking-wider">Legacy addon</div>
                <div className="text-zita-espresso font-medium">{u.collections_addon_active ? 'Grandfathered' : '—'}</div>
              </div>
              <div className="zita-card p-3">
                <div className="text-zita-mocha uppercase tracking-wider">Created</div>
                <div className="text-zita-espresso font-medium">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</div>
              </div>
              <div className="zita-card p-3">
                <div className="text-zita-mocha uppercase tracking-wider">Last login</div>
                <div className="text-zita-espresso font-medium">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}</div>
              </div>
            </div>

            <div className="zita-card p-3">
              <div className="text-xs uppercase tracking-wider text-zita-mocha mb-2">Activity</div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-zita-mocha text-[10px]">Scans</div>
                  <div className="font-medium text-zita-espresso">{c.scans}</div>
                </div>
                <div>
                  <div className="text-zita-mocha text-[10px]">Listings</div>
                  <div className="font-medium text-zita-espresso">{c.listings}</div>
                </div>
                <div>
                  <div className="text-zita-mocha text-[10px]">Saved</div>
                  <div className="font-medium text-zita-espresso">{c.saved}</div>
                </div>
                <div>
                  <div className="text-zita-mocha text-[10px]">Collections</div>
                  <div className="font-medium text-zita-espresso">{c.collections}</div>
                </div>
                <div>
                  <div className="text-zita-mocha text-[10px]">Tickets</div>
                  <div className="font-medium text-zita-espresso">{c.tickets}</div>
                </div>
                <div>
                  <div className="text-zita-mocha text-[10px]">Open tix</div>
                  <div className="font-medium text-zita-espresso">{c.open_tickets}</div>
                </div>
              </div>
              <div className="text-xs text-zita-mocha mt-2">
                Last scan {detail.last_scan_at ? new Date(detail.last_scan_at).toLocaleString() : '—'} ·
                {' '}Last ticket {detail.last_ticket_at ? new Date(detail.last_ticket_at).toLocaleString() : '—'}
              </div>
            </div>

            {u.is_admin ? (
              <div className="text-xs text-zita-mocha bg-zita-sand/50 p-3 rounded-lg">
                Super Admin account — protected. Suspension and role changes are disabled for the sole owner identity.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {!u.email_verified && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={forceVerify}
                    data-testid="admin-user-force-verify"
                    className="zita-btn-secondary text-xs inline-flex items-center gap-1"
                  >
                    <MailCheck size={12} /> Force-verify email
                  </button>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => patch({ suspended: !u.suspended })}
                  data-testid="admin-user-suspend-drawer"
                  className="zita-btn-secondary text-xs inline-flex items-center gap-1"
                >
                  {u.suspended ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
                  {u.suspended ? 'Un-suspend' : 'Suspend'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setMsgOpen((v) => !v)}
                  data-testid="admin-user-message-toggle"
                  className="zita-btn-primary text-xs inline-flex items-center gap-1"
                >
                  <Send size={12} /> Send direct message
                </button>
              </div>
            )}

            {msgOpen && (
              <div className="zita-card p-4 space-y-2" data-testid="admin-user-message-form">
                <input
                  value={msgSubject}
                  onChange={(e) => setMsgSubject(e.target.value)}
                  placeholder="Subject"
                  data-testid="admin-user-message-subject"
                  className="zita-input w-full px-3 py-2 text-sm"
                />
                <textarea
                  rows={4}
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  placeholder="Your message to the user. Delivered in-app and via email."
                  data-testid="admin-user-message-body"
                  className="zita-input w-full px-3 py-2 text-sm"
                />
                <div className="flex items-center gap-2">
                  <select
                    value={msgSeverity}
                    onChange={(e) => setMsgSeverity(e.target.value)}
                    className="zita-input px-2 py-1 text-xs"
                    data-testid="admin-user-message-severity"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={busy || !msgSubject.trim() || !msgBody.trim()}
                    data-testid="admin-user-message-send"
                    className="zita-btn-primary text-xs ml-auto"
                  >
                    Send message
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

export const AdminUsers = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/admin/users');
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = async (uid, updates) => {
    try {
      await api.patch(`/admin/users/${uid}`, updates);
      toast.success('Updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const filtered = q
    ? items.filter((u) =>
        (u.email || '').toLowerCase().includes(q.toLowerCase()) ||
        (u.display_name || '').toLowerCase().includes(q.toLowerCase()) ||
        (u.username || '').toLowerCase().includes(q.toLowerCase())
      )
    : items;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-zita-espresso">Users</h1>
          <p className="text-xs text-zita-mocha mt-1">Click a row for full detail, direct messaging, and account controls.</p>
        </div>
        <input
          data-testid="admin-users-search"
          type="search"
          placeholder="Search email, name, username"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="zita-input px-3 py-2 text-sm w-64"
        />
      </div>
      <div className="zita-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zita-sand/50 text-zita-mocha uppercase tracking-wider text-[11px]">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Subscription</th>
              <th className="text-left px-4 py-3">Verified</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-users-tbody">
            {busy && !filtered.length && (
              <tr><td colSpan="6" className="px-4 py-6 text-center text-zita-mocha">Loading…</td></tr>
            )}
            {filtered.map((u) => (
              <tr
                key={u.id}
                className="border-t border-zita-taupe/40 hover:bg-zita-sand/30 cursor-pointer"
                onClick={() => setSelectedId(u.id)}
                data-testid={`admin-user-row-${u.id}`}
              >
                <td className="px-4 py-3 text-zita-espresso">
                  <div className="font-medium">{u.email}</div>
                  {u.display_name && <div className="text-xs text-zita-mocha">{u.display_name}</div>}
                  {u.username && <div className="text-xs text-zita-cocoa">@{u.username}</div>}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full bg-zita-sand text-zita-cocoa">
                    {u.is_admin ? 'super_admin' : u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-zita-mocha">{u.subscription_status || 'none'}</td>
                <td className="px-4 py-3 text-zita-mocha">
                  {u.email_verified ? (
                    <span className="text-emerald-800">verified</span>
                  ) : (
                    <span className="text-amber-800">pending</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-zita-mocha">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                </td>
                <td className="px-4 py-3 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                  {!u.is_admin && (
                    <>
                      <button
                        type="button"
                        onClick={() => patch(u.id, { suspended: !u.suspended })}
                        className="text-xs px-2 py-1 rounded-md border border-zita-taupe hover:bg-zita-sand"
                        data-testid={`admin-user-suspend-${u.id}`}
                      >
                        {u.suspended ? 'Un-suspend' : 'Suspend'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedId(u.id)}
                        className="text-xs px-2 py-1 rounded-md border border-zita-taupe hover:bg-zita-sand"
                        data-testid={`admin-user-open-${u.id}`}
                      >
                        Open
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <UserDetailDrawer
          userId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default AdminUsers;
